import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import {
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Typography } from "../../atoms/Typography/Typography";
import { Label } from "../../atoms/Label/Label";
import { escapeRegExp } from "../../../utils/escapeRegex";
import { Input } from "../../atoms/Input";
import { Controller, type ControllerProps } from "react-hook-form";

const fieldWrapperVariants = cva(null, {
  variants: {
    labelBehaviour: { floating: "relative", placeholder: "" },
    size: { sm: null, md: null, lg: null },
  },
  defaultVariants: { labelBehaviour: "floating", size: "sm" },
});

const labelVariants = cva(null, {
  variants: {
    labelBehaviour: {
      floating:
        "pointer-events-none absolute transition-all text-font-secondary \
        left-[0.55rem] top-1/2 -translate-y-1/2 \
        peer-focus:top-1 peer-[&:not(:placeholder-shown)]:top-1 leading-none",
      placeholder: "",
    },
    size: { sm: null, md: null, lg: null },
  },
  compoundVariants: [
    {
      labelBehaviour: "floating",
      size: "sm",
      class: `
        peer-placeholder-shown:text-sm
        peer-focus:text-[0.625rem]
        peer-[&:not(:placeholder-shown)]:text-[0.625rem]
        peer-focus:translate-y-0.5
        peer-[&:not(:placeholder-shown)]:translate-y-0.5
      `,
    },
    {
      labelBehaviour: "floating",
      size: "md",
      class: `
        peer-placeholder-shown:text-base
        peer-focus:text-xs
        peer-[&:not(:placeholder-shown)]:text-xs
        peer-focus:translate-y-1
        peer-[&:not(:placeholder-shown)]:translate-y-1
      `,
    },
    {
      labelBehaviour: "floating",
      size: "lg",
      class: `
        peer-placeholder-shown:text-lg
        peer-focus:text-sm
        peer-[&:not(:placeholder-shown)]:text-sm
        peer-focus:translate-y-1
        peer-[&:not(:placeholder-shown)]:translate-y-1
      `,
    },
  ],
  defaultVariants: { size: "sm", labelBehaviour: "floating" },
});

const comboboxInputVariants = cva(
  "peer w-full px-2 border border-border box-border text-font-primary placeholder-font-secondary",
  {
    variants: {
      labelBehaviour: {
        floating: "peer",
        placeholder: null,
      },
      size: { sm: null, md: null, lg: null },
    },
    compoundVariants: [
      {
        labelBehaviour: "floating",
        size: "sm",
        class: `pt-3`,
      },
      {
        labelBehaviour: "floating",
        size: "md",
        class: "pt-4",
      },
      {
        labelBehaviour: "floating",
        size: "lg",
        class: "pt-5",
      },
    ],
    defaultVariants: {
      size: "sm",
      labelBehaviour: "floating",
    },
  }
);

const optionsPanelVariants = cva(
  "w-(--input-width) mt-1 max-h-64 overflow-auto z-50 \
  border border-border rounded-md bg-white shadow-lg \
  empty:invisible",
  {
    variants: { size: { sm: "text-sm", md: "text-base", lg: "text-lg" } },
    defaultVariants: { size: "sm" },
  }
);

const optionItemVariants = cva(
  "cursor-pointer px-2 py-2 w-full \
  data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed \
  data-[focus]:bg-neutral-100 data-[selected]:font-medium",
  {
    variants: { size: { sm: "text-sm", md: "text-base", lg: "text-lg" } },
    defaultVariants: { size: "sm" },
  }
);

type FieldCva = VariantProps<typeof fieldWrapperVariants>;

export type Option = {
  label: string;
  value: string | number;
  image?: string;
  leftItem?: ReactNode;
  rightItem?: ReactNode;
  disabled?: boolean;
  [k: string]: string | undefined | ReactNode;
};

export type SingleProps = {
  multiple?: false;
  value?: Option | null;
  onChange: (v?: Option | null) => void;
};

export type MultiProps = {
  multiple: true;
  value: Option[];
  onChange: (v: Option[]) => void;
};

export type ComboboxFieldProps = SingleProps & {
  id?: string;
  label?: string;
  items: Option[];
  value?: Option | null;
  renderOption?: (
    option: Option,
    state: { focus: boolean; selected: boolean; disabled: boolean }
  ) => React.ReactNode;
  onChange: (value?: Option | null) => void;
  customFilter?: (items: Option[], query: string) => Option[];
  getLabel?: (option: Option) => string | React.ReactElement;
  placeholder?: string;
  error?: string;
  hint?: string;
  className?: string;
  openOnFocus?: boolean;
  inputProps?: Omit<
    ComponentPropsWithoutRef<"input">,
    "id" | "value" | "onChange" | "placeholder" | "className" | "defaultValue"
  >;

  virtualized?: boolean;
  virtualThreshold?: number;
} & Pick<FieldCva, "labelBehaviour" | "size">;

export function ComboboxField({
  id,
  label,
  labelBehaviour = "floating",
  size = "sm",
  items,
  value,
  onChange,
  customFilter,
  placeholder = "Search or select…",
  error,
  hint,
  renderOption,
  getLabel = (option: Option) => option.label,
  openOnFocus = true,
  className,
  inputProps,
  virtualized = false,
  virtualThreshold,
}: ComboboxFieldProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query) return items;
    if (customFilter) return customFilter(items, query);
    const re = new RegExp(escapeRegExp(query), "i");
    return items.filter((item) => re.test(item.label));
  }, [items, query, customFilter]);

  const isFloating = labelBehaviour === "floating";

  const displayValue = (val: Option | Option[] | null) =>
    Array.isArray(val) ? val.map((v) => v.label).join(", ") : val?.label ?? "";

  const useVirtual =
    virtualized && (virtualThreshold ? items.length > virtualThreshold : true);
  const virtualProp = useVirtual ? { options: filtered } : undefined;
  return (
    <div
      className={twMerge(
        fieldWrapperVariants({ labelBehaviour, size }),
        className
      )}
    >
      <Combobox<Option | null>
        value={value ?? null}
        onChange={onChange}
        immediate={openOnFocus}
        onClose={() => setQuery("")}
        by={(a, b) => a?.value === b?.value}
        virtual={virtualProp}
      >
        <ComboboxInput
          as={Input}
          id={id}
          invalid={!!error}
          placeholder={isFloating ? " " : placeholder}
          displayValue={displayValue}
          onChange={(e) => setQuery(e.target.value)}
          className={twMerge(
            comboboxInputVariants({
              size,
              labelBehaviour,
            })
          )}
          inputSize={size}
          autoComplete="off"
          {...inputProps}
        />

        {label && (
          <Label
            htmlFor={id}
            className={twMerge(
              labelVariants({ size, labelBehaviour }),
              !isFloating && "hidden"
            )}
          >
            {label}
          </Label>
        )}

        <ComboboxOptions
          anchor="bottom"
          className={optionsPanelVariants({ size })}
        >
          {useVirtual ? (
            ({ option }) => {
              return (
                <ComboboxOption
                  key={option.value}
                  value={option}
                  className={optionItemVariants({ size })}
                >
                  {({ focus, selected, disabled }) => (
                    <>
                      {renderOption
                        ? renderOption(option, { focus, selected, disabled })
                        : getLabel(option)}
                    </>
                  )}
                </ComboboxOption>
              );
            }
          ) : filtered.length === 0 ? (
            <div className="px-2 py-2 text-font-secondary">No matches</div>
          ) : (
            filtered.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className={optionItemVariants({ size })}
              >
                {({ focus, selected, disabled }) => (
                  <>
                    {renderOption
                      ? renderOption(option, { focus, selected, disabled })
                      : getLabel(option)}
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>

      {hint && !error && (
        <Typography className="text-xs absolute mt-0.5" color="secondary">
          {hint}
        </Typography>
      )}
      {error && (
        <Typography color="danger" className="text-xs absolute mt-0.5">
          {error}
        </Typography>
      )}
    </div>
  );
}

export type FormComboBoxFieldProps = Omit<
  ComboboxFieldProps,
  "onChange" | "value"
> & {
  name: string;
  controller?: Pick<
    ControllerProps,
    "defaultValue" | "disabled" | "rules" | "shouldUnregister"
  >;
};

export function FormComboboxField({
  name,
  controller,
  inputProps,
  items = [],
  ...restProps
}: FormComboBoxFieldProps) {
  const optionMap = useMemo(() => {
    const map = new Map<Option["value"], Option>();
    for (const option of items) {
      map.set(option.value, option);
    }
    return map;
  }, [items]);

  const isOption = (candidate: unknown): candidate is Option =>
    Boolean(
      candidate &&
        typeof candidate === "object" &&
        "value" in (candidate as Option) &&
        "label" in (candidate as Option)
    );

  const toOption = (val: unknown): Option | null => {
    if (isOption(val)) return val;
    if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "bigint"
    ) {
      return optionMap.get(val as Option["value"]) ?? null;
    }
    return null;
  };

  const toPrimitive = (candidate: Option | null | undefined) =>
    candidate == null ? "" : candidate.value;

  return (
    <Controller
      {...controller}
      name={name}
      render={({ field: { value, onChange, onBlur } }) => {
        const selectedOption = toOption(value);

        return (
          <ComboboxField
            {...restProps}
            items={items}
            value={selectedOption}
            onChange={(option) => onChange(toPrimitive(option))}
            inputProps={{
              ...(inputProps ?? {}),
              onBlur: (event) => {
                inputProps?.onBlur?.(event);
                onBlur();
              },
            }}
          />
        );
      }}
    />
  );
}
